import BaseExportStrategy from './BaseExportStrategy';

/**
 * Estrategia de exportación para reportes de Info Mora (Financiamiento)
 * Define las columnas y el formato específico para Info Mora
 */
export default class InfoMoraExportStrategy extends BaseExportStrategy {
  constructor() {
    super('Info Mora (Financiamiento)');
  }

  getTitle(context) {
    return 'INFORME DE MORA';
  }

  /**
   * Define las columnas del reporte de Info Mora
   */
  getColumns(options = {}) {
    const columns = [];

    // 1. Legajo
    columns.push({
      key: 'legajo',
      header: 'Legajo',
      pdfWidth: 15,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.legajo || '-',

      formatSubtotal: (item, ctx) => 'SUBTOTAL',

      formatTotal: (items, ctx) => ''  // Vacío - el texto va en Fideicomiso
    });

    // 2. Fideicomiso
    columns.push({
      key: 'fideicomiso',
      header: 'Fideicomiso',
      pdfWidth: 30,
      excelWidth: 30,
      align: 'left',

      format: (item, ctx) => item.fideicomiso || item.fideicomisoName || '-',

      formatSubtotal: (item, ctx) => `${item._cantidadRegistros || 0} registros`,

      formatTotal: (items, ctx) => 'TOTAL - RESUMEN'  // Texto aquí
    });

    // 3. Operatoria / Línea
    columns.push({
      key: 'operatoria_linea',
      header: 'Operatoria / Línea',
      pdfWidth: 38,
      excelWidth: 35,
      align: 'left',

      format: (item, ctx) => item.operatoria_linea || '-',

      formatSubtotal: (item, ctx) => {
        return (item._subtotales && item._subtotales.operatoria) || item.operatoria || '-';
      },

      formatTotal: (items, ctx) => ''
    });

    // 4. Apellido y Nombre / Razón Social
    columns.push({
      key: 'nombre_razon_social',
      header: 'Apellido y Nombre / Razón Social',
      pdfWidth: 40,
      excelWidth: 40,
      align: 'left',

      format: (item, ctx) => {
        if (!item.nombre_razon_social) return '-';
        return item.nombre_razon_social.toUpperCase();
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 5. CUIT
    columns.push({
      key: 'cuit',
      header: 'CUIT',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => item.cuit || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 6. Departamento
    columns.push({
      key: 'departamento',
      header: 'Departamento',
      pdfWidth: 25,
      excelWidth: 20,
      align: 'left',

      format: (item, ctx) => {
        // ✅ PRIMERO: Verificar si el departamento ya viene del backend
        if (item.departamento && item.departamento !== '') {
          return item.departamento;
        }
        // Intentar obtener departamento desde diferentes estructuras
        if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
          return item.localidad.departamento.nombre;
        }
        if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
          return item.cliente.localidad.departamento.nombre;
        }
        // Fallback a localidad si no hay departamento
        if (item.cliente && item.cliente.localidad) {
          return item.cliente.localidad;
        }
        if (item.localidad && typeof item.localidad === 'string') {
          return item.localidad;
        }
        if (item.localidad && item.localidad.nombre) {
          return item.localidad.nombre;
        }
        return '-';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 7. Persona
    columns.push({
      key: 'persona',
      header: 'Persona',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.persona || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 8. Fecha de Ingreso
    columns.push({
      key: 'fecha_ingreso',
      header: 'Fecha de Ingreso',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => this.formatDates(item.fecha_ingreso),

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 9. Estado
    columns.push({
      key: 'estado',
      header: 'Estado',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.estado || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 10. Monto ANR
    columns.push({
      key: 'monto_anr',
      header: 'Monto ANR',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.monto_anr) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.monto_anr) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.monto_anr) || 0;
          return total + monto;
        }, 0);
      }
    });

    // 11. Monto Crédito (calculado como Monto Otorgado - Monto ANR)
    columns.push({
      key: 'monto_credito',
      header: 'Monto Crédito',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const montoOtorgado = parseFloat(item.monto) || 0;
        const montoAnr = parseFloat(item.monto_anr) || 0;
        const montoCredito = montoOtorgado - montoAnr;
        return this.formatMonto(montoCredito);
      },

      formatSubtotal: (item, ctx) => {
        const montoOtorgado = (item._subtotales && item._subtotales.monto) || 0;
        const montoAnr = (item._subtotales && item._subtotales.monto_anr) || 0;
        const montoCredito = montoOtorgado - montoAnr;
        return this.formatMonto(montoCredito);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const montoOtorgado = parseFloat(item.monto) || 0;
          const montoAnr = parseFloat(item.monto_anr) || 0;
          const montoCredito = montoOtorgado - montoAnr;
          return total + montoCredito;
        }, 0);
      }
    });

    // 12. Monto Otorgado
    columns.push({
      key: 'monto_otorgado',
      header: 'Monto Otorgado',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.monto) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.monto) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.monto) || 0;
          return total + monto;
        }, 0);
      }
    });

    // 13. Fecha de Otorgamiento
    columns.push({
      key: 'fecha_otorgamiento',
      header: 'Fecha de Otorgamiento',
      pdfWidth: 22,
      excelWidth: 16,
      align: 'left',

      format: (item, ctx) => item.fecha_otorgamiento || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 14. Cantidad Cuotas
    columns.push({
      key: 'cantidad_cuotas',
      header: 'Cantidad Cuotas',
      pdfWidth: 20,
      excelWidth: 14,
      align: 'center',

      format: (item, ctx) => item.cantidad_cuotas || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 15. Saldo Capital
    columns.push({
      key: 'saldo_capital',
      header: 'Saldo Capital',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.saldo_capital) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.saldo_capital) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.saldo_capital) || 0;
          return total + monto;
        }, 0);
      }
    });

    // 16. Monto Cobrado
    columns.push({
      key: 'monto_cobrado',
      header: 'Monto Cobrado',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.monto_cobrado) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.monto_cobrado) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.monto_cobrado) || 0;
          return total + monto;
        }, 0);
      }
    });

    // 17. Cuotas en Mora
    columns.push({
      key: 'cantidad_cuotas_mora',
      header: 'Cuotas en Mora',
      pdfWidth: 20,
      excelWidth: 14,
      align: 'center',

      format: (item, ctx) => item.cantidad_cuotas_mora || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 18. Monto Vencido (Capital Vencido)
    columns.push({
      key: 'capital_vencido',
      header: 'Monto Vencido',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.capital_vencido) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.capital_vencido) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.capital_vencido) || 0;
          return total + monto;
        }, 0);
      }
    });

    // 19. Monto en Mora
    columns.push({
      key: 'monto_mora',
      header: 'Monto en Mora',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.monto_mora) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.monto_mora) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = parseFloat(item.monto_mora) || 0;
          return total + monto;
        }, 0);
      }
    });

    // 20. Días de Mora
    columns.push({
      key: 'dias_mora',
      header: 'Días de Mora',
      pdfWidth: 20,
      excelWidth: 14,
      align: 'center',

      format: (item, ctx) => {
        if (!item.dias_mora) return '-';
        return `${item.dias_mora} días`;
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => {
        // Calcular promedio de días de mora para el total
        const itemsConMora = items.filter(item => item.dias_mora && item.dias_mora > 0);
        if (itemsConMora.length === 0) return '0 días';

        const suma = itemsConMora.reduce((total, item) => {
          return total + (parseFloat(item.dias_mora) || 0);
        }, 0);

        const promedio = Math.round(suma / itemsConMora.length);
        return `${promedio} días-Promedio`;
      }
    });

    // 21. Estado Morosidad
    columns.push({
      key: 'estado_morosidad',
      header: 'Estado Morosidad',
      pdfWidth: 20,
      excelWidth: 14,
      align: 'left',

      format: (item, ctx) => item.estado_morosidad || 'S/E',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 22. Porcentaje de Mora
    columns.push({
      key: 'porcentaje_mora',
      header: 'Porcentaje de Mora',
      pdfWidth: 20,
      excelWidth: 14,
      align: 'center',

      format: (item, ctx) => {
        const montoMora = parseFloat(item.monto_mora) || 0;
        const capitalVencido = parseFloat(item.capital_vencido) || 0;

        if (montoMora === 0 || capitalVencido === 0) return '0%';

        const porcentaje = (montoMora / capitalVencido) * 100;
        return `${porcentaje.toFixed(2)}%`;
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 23. Estado Manual
    columns.push({
      key: 'estado_manual',
      header: 'Estado Manual',
      pdfWidth: 20,
      excelWidth: 14,
      align: 'left',

      format: (item, ctx) => item.estado_manual || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    return columns;
  }

  /**
   * Info Mora no usa desembolsos dinámicos
   */
  getMaxDesembolsos(options) {
    return 0;
  }
}
